import React, { useState, useEffect, useRef } from 'react';
import styles from './ValuationWidget.scss';
import ButtonRound from '../ButtonRound';
import History from '../../utils/BrowserHistory';
import ValuationApi, { IValuationLightResult } from './ValuationApi';
import LoadingIndicator from '../LoadingIndicator';
import Check from '../Check';
import stepsDefault, { IStep } from './ValuationSteps';
import stepsDashboard from './ValuationStepsDashboard';
import Image from '../Image';
import ValuationCode from './ValuationCode';
import ValuationOptions from './ValuationOptions';
// import NumberFormat from 'react-number-format';
import Input from './Input';
import Contact from './Contact';
import Precalc from './Precalc';
import ValuationRequest, { IValuationRequestAdd } from '../../utils/ValuationRequest';
import Tooltip from '../Tooltip';
import LoginRegister from './LoginRegister';
import useUser from '../../stores/useUser';
import HtmlElement from '../HtmlElement';
import winOpen from '../../utils/openWindowWithLoadingIndicator';
import DossierButton from './DossierButton';
import AppState from '../../utils/AppState';
import NumberField from '../MaterialUi/NumberField';
import { createToast } from '../MaterialUi/Toast';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import trackApi from '../../utils/trackApi';
import { capitalize } from 'lodash';
import LogError from '../../utils/LogError';
import { inputPatterns } from '../../utils/inputPatterns';

/* TODO: rename to: valuation */

export interface IValuationWidgetProps {
    className?: string;

    /**
     * step for first render
     */
    startStep?: number;

    /**
     * default: Makler + Link auf Pricehubble
     * dashboard: ohne Makler, dafür login/register, dann redirect zu Dashboard
     */
    variant?: 'default' | 'dashboard';

    /**
     * function for step0 back button
     */
    backAction?: () => void
}

const testData = {
    code: 'house',
    subcode: 'house_row_corner',
    livingArea: 172,
    landArea: 236,
    buildingYear: 2002,
    numberOfRooms: 5,
    numberOfBathrooms: 3,
    condition: 'well_maintained',
    hasGarage: 1,
    hasParkingSpace: 1,
    postCode: '76189',
    city: 'Karlsruhe',
    street: 'Friedrich-Weick-Str.',
    houseNumber: '72',
    salutation: 'Herr',
    firstName: 'Falk',
    lastName: 'Brückner',
    email: 'fabr@fabid.de',
    reason: 'sell',
    estateAgent: 'yes',

    dealType: 'sale',
    countryCode: 'DE',
};

/*
  building_year: 2000
  city: "Berlin"
  code: "house"
  condition: "well_maintained"
  country_code: "DE"
  email: "fabr@gmx.de"
  first_name: "Flak"
  has_garage: 1
  has_parking_space: 1
  house_number: "23"
  land_area_sqm: 400
  lang: "de"
  last_name: "s"
  latitude: 52.4849318
  living_area_sqm: 164
  locale: "de-DE"
  longitude: 13.4280197
  number_of_bathrooms: 1
  number_of_rooms: 2
  phone_number: "123456789"
  post_code: "12043"
  reason: "just_looking"
  salutation: "Herr"
  street: "Karl-Marx-Straße"
  subcode: "house_row_corner"
*/

export type ICondition = 'renovation_needed' | 'well_maintained' | 'new_or_recently_renovated';
export type IQuality = 'simple' | 'normal' | 'high_quality' | 'luxury';

export type ISubcode =
    | 'apartment_normal'
    | 'apartment_maisonette'
    | 'apartment_attic'
    | 'apartment_penthouse'
    | 'apartment_terraced'
    | 'apartment_studio'
    | 'house_detached'
    | 'house_semi_detached'
    | 'house_row_corner'
    | 'house_row_middle'
    | 'house_farm';

type ISalutation = 'Frau' | 'Herr' | ''; // not in https://docs.pricehubble.com/#types

// type IReason = 'buy' | 'sell' | 'just_looking' | 'other'; // not in https://docs.pricehubble.com/#types

type IReason =
    | 'buy'
    | 'sell'
    | 'rent' // mieten
    | 'let'; // vermieten;

type ISellingUrgency = 'urgent' | 'within_next_year' | 'in_several_years';

export type ICode = 'house' | 'apartment';
type ICountryCode = 'DE';

type IDealType = 'sale' | 'rent';

type IEstateAgent = 'yes' | 'maybe' | 'no';

// const x = {
//     buildingYear: Math.round(parseFloat(data.baujahr)),
//     city: data.locality,
//     code: data.property_type_parent_name,
//     condition: undefined,
//     countryCode: 'DE',
//     dealType: data.type_of_marketing === 'buy' ? 'sale' : 'rent',
//     houseNumber: undefined,
//     landArea: Math.round(parseFloat(data.plot_area)),
//     livingArea: Math.round(parseFloat(data.living_area)),
//     numberOfRooms: parseFloat(data.chamber_count),
//     numberOfBathrooms: Math.round(parseFloat(data.bathroom_count)),
//     postCode: data.field_address.postal_code,
//     street: data.field_address.address_line1,
//     subcode: this.propertyType2subcode(data.property_type_name),
// };

export type IValuationLightData = {
    buildingYear: number;
    city: string;
    code: ICode;
    condition: ICondition;
    countryCode?: ICountryCode;
    houseNumber?: string;
    landArea: number;
    livingArea: number;
    numberOfRooms: number;
    numberOfBathrooms: number;
    postCode?: string;
    reason?: IReason;
    street: string;
    subcode: ISubcode;
};

export type IValuationContact = {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    salutation: ISalutation; // not in https://docs.pricehubble.com/#types
    dataProcessingAccepted?: boolean;
};

export type IValuationData = Partial<IValuationContact> &
    IValuationLightData & {
        id?: string;

        dealType?: IDealType;
        hasGarage: number;
        hasParkingSpace: number;
        lang?: string; // TODO
        latitude?: number; // TODO
        locale?: string; // TODO
        longitude?: number; // TODO
        // reason?: IReason; // not in https://docs.pricehubble.com/#types
        estateAgent?: IEstateAgent;
        estateAgentNo?: IEstateAgent;

        dossier_id?: string;
        dossier_url?: string;
        dossier_url_valid_until?: string; //'2022-06-14T11:19:23+02:00';

        /* valuation updates in dasboard */
        sendUpdates?: boolean;

        /* when reason is sell */
        sellingUrgency?: ISellingUrgency; // TODO
        propertyPrice?: number; // TODO

        /* precalc done */
        precalc?: number;
    };

type IScore = {
    // confidence: string; // 'good'
    // currency: string;
    // valueRange: {
    //     lower: number;
    //     upper: number;
    // };
    detailUrl: string;
} & IValuationLightResult;

export const i18n = {
    house: {
        code: 'Haus',
        codeOf: 'des Hauses',
        theCode: 'das Haus',
        toCode: 'zum Haus',
    },
    apartment: {
        code: 'Wohnung',
        codeOf: 'der Wohnung',
        theCode: 'die Wohnung',
        toCode: 'zur Wohnung',
    },
};

const replaceString = (str: string = '', code: IValuationData['code'] = null) => {
    const templateVars = str.match(/\{(.*?)\}/g);
    if (templateVars) {
        templateVars.map((val) => {
            const v = val.replace('{', '').replace('}', '');
            if (i18n[v]) {
                str = str.replace(val, i18n[v]);
            } else if (code && i18n[code] && i18n[code][v]) {
                str = str.replace(val, i18n[code][v]);
            }
        });
    }
    return str;
};

const isReasonSell = (step: IStep, data: IValuationData) => {
    return !step.usedBy || !step.usedBy.reason || !data.reason || step.usedBy.reason.includes(data.reason);
};

const isCode = (step: IStep, data: IValuationData) => {
    return !step.usedBy || !step.usedBy.code || step.usedBy.code.includes(data.code);
};

const isRealEstateAgentAnswerNo = (step: IStep, data: IValuationData) => {
    return !step.usedBy || !step.usedBy.estateAgent || !data.estateAgent || step.usedBy.estateAgent.includes(data.estateAgent);
};

const renderScore = (data) => {
    const steps = stepsDefault;

    return (
        <div className={styles.summary}>
            <b>Deine Angaben</b>:
            <br />
            <ul>
                <li>{steps.find((s) => s.name === 'subcode').options[data.code][data.subcode]}</li>
                <li>{data.livingArea} m² Wohnfläche</li>
                {data.code === 'house' && <li>{data.landArea} m² Grundstücksfläche</li>}
                <li>Baujahr {data.buildingYear}</li>
                <li>{data.numberOfRooms} Zimmer</li>
                <li>{data.numberOfBathrooms} Badezimmer</li>
                <li>Zustand: {steps.find((s) => s.name === 'condition').options[data.code][data.condition]}</li>
                <li>
                    <ul>
                        <li>{data.hasGarage && steps.find((s) => s.name === 'features').features[data.code].hasGarage}</li>
                        <li>{data.hasParkingSpace && steps.find((s) => s.name === 'features').features[data.code].hasParkingSpace}</li>
                    </ul>
                </li>
                <li>
                    <p>
                        <b>Adresse:</b>
                        <br />
                        {data.street}
                        <br />
                        {data.postCode} {data.city}
                        <br />
                    </p>
                </li>
            </ul>
        </div>
    );
};

const sendToDnz = (data, variant: IValuationWidgetProps['variant']): Promise<IValuationRequestAdd> => {
    return ValuationRequest.add(data, variant)
        .then((res) => {
            return res;
        })
        .catch((e) => {
            console.error(e);
            return null;
        });
};

const trackStep = (trackingName: string, data: IValuationData) => {

    if (!trackingName) {
        return
    }

    if (trackingName.includes('|')) {
        trackingName = trackingName.split('|')[data?.code === 'house' ? 0 : 1];
    }

    const type = data?.code === 'house' ? 'Haus' : data?.code === 'apartment' ? 'Wohnung' : 'Nicht befuellt';

    trackApi.wglPageview(`/user/dashboard/create/${trackingName}`, `Dashboard erstellen, ${capitalize(trackingName)}`, { "streckenzweig1": type })
}

const ValuationWidget: React.FC<React.PropsWithChildren<IValuationWidgetProps>> = ({ startStep = 0, variant = 'default', className = null, backAction }) => {
    // const [data, setData] = useState<IValuationData>(testData as IValuationData); //
    const defaultData = { code: null, countryCode: 'DE' } as IValuationData;
    const [data, setData] = useState<IValuationData>(defaultData);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [valuationId, setValuationId] = useState<string>(null);

    const [stepIdx, setStepIdx] = useState<number>(startStep);
    const [score, setScore] = useState<IScore>(null);

    const topRef = useRef<HTMLHeadingElement>();

    const steps = variant === 'default' ? stepsDefault : stepsDashboard;
    const step = steps[stepIdx];

    const [user] = useUser();

    React.useEffect(() => {
        const step = location.hash ? parseInt(location.hash.replace('#step', '')) : null;
        if (step !== startStep) {
            History.push({
                hash: `#step${startStep || '0'}`,
            });
        }

        trackStep(steps[startStep || 0]?.trackingName, undefined);

        // scroll to Widget, when Widget is initially rendered with step1 (not step0)
        if (startStep === 1) {
            setTimeout(() => {
                topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }, 0);
        }

        const unlisten = History.listen(({ location, action }) => {
            const step = location.hash ? parseInt(location.hash.replace('#step', '')) : 0;
            setStepIdx(step);

            setTimeout(() => {
                topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }, 0);
        });
        return unlisten;
    }, []);

    const nextStep = (name: IStep['name'] = null, value: string | number = null) => {
        // const currCode = name === 'code' ? (value as IValuationData['code']) : data.code;
        const currData = { ...data };
        if (name && value) {
            if (name === 'code') {
                data.subcode = null;
                data.landArea = null;
            }
            currData[name] = value;
            // setData({ ...data, [name]: value });
        }

        const nextIdx = steps.findIndex(
            (s, idx) => idx > stepIdx && isCode(s, currData) && isReasonSell(s, currData) && isRealEstateAgentAnswerNo(s, currData)
        );
        if (nextIdx > 0) {
            History.push({
                hash: '#step' + nextIdx,
            });
            setStepIdx(nextIdx);

            trackStep(steps[nextIdx]?.trackingName, currData);
            // trackApi.wglPageview()
        }
        setData(currData);
    };

    const handleSubmit = (ev: React.FormEvent) => {
        ev.preventDefault();

        if (step.isLast && variant === 'default') {
            nextStep();
            sendData();
        } else {
            nextStep();
        }
    };

    const sendData = () => {
        setIsLoading(true);

        setTimeout(() => {
            trackStep('ende', data);
        }, 0)

        Promise.all([sendToDnz(data, variant), sendToPricehubble()])
            .then(([dnzRes, pricehubbleRes]) => {
                if (variant === 'default') {
                    setValuationId(dnzRes?.id);
                    ValuationRequest.add({
                        ...data,
                        id: dnzRes?.id,
                        dossierUrl: pricehubbleRes?.detailUrl,
                        valueRange: pricehubbleRes?.valueRange,
                    }).catch((e) => {
                        console.error(e);
                    });
                } else {
                    const href = `/user/${user.uid}/dashboard/${dnzRes?.id ? dnzRes?.id : ''}`;
                    // History.push(href); // React reroute
                    location.assign(href); // Redirect to new Drupal site
                }
            })
            .catch((e) => {
                console.error(e);
            })
            .then(() => {
                setIsLoading(false);
            });
    };

    const prevStep = () => {
        let prevIdx = 0;
        steps.findIndex((s, idx) => {
            if (idx < stepIdx) {
                prevIdx = isCode(s, data) && isReasonSell(s, data) && isRealEstateAgentAnswerNo(s, data) ? idx : prevIdx;
            }
            return stepIdx === idx;
        });

        if (prevIdx < (startStep || 0) && typeof backAction === 'function') {
            backAction()
            return
        }

        trackStep(steps[prevIdx]?.trackingName, data);

        History.back();
        setStepIdx(prevIdx);

        if (steps[prevIdx].name === 'precalc') {
            History.back();
        }
    };

    const sendToPricehubble = (): Promise<IValuationLightResult & { detailUrl: string }> => {
        if (variant !== 'default') {
            return Promise.resolve(null);
        }

        return ValuationApi.getValuatationLight(ValuationApi.data2lightApi(data))
            .then((valData) => {
                setScore({ ...valData, detailUrl: null });
            })
            .catch((e) => {
                console.error(e);
                createToast('Fehler beim Aufbereiten der Daten.', 'error');
                LogError.add(e, {
                    title: 'Fehler beim Aufbereiten der Daten',
                    error_code: 'ValuationWidget.tsx:sendToPricehubble',
                    request_data: { data },
                });
                return null;
            });
    };

    // const goToDashboard = () => {};

    const onChange = (name: IStep['name'], value: string | boolean) => {
        if (variant === 'dashboard' && step.isLast && user.isLoggedIn && user.isInitialized) {
            if (!AppState.get('wasPersistedPageShow')) {
                sendData();
            }
        } else {
            setData({ ...data, [name]: value });
        }
    };

    // const showNextButton = data[step.name] || !step.options || step.features;
    // const showNextButton = data[step.name] || stepIdx > 0;
    const isFullAdress = step.name === 'address' && data.postCode && data.city && data.street;
    const withEstateAgent = data.reason === 'sell' && (data.estateAgent === 'yes' || data.estateAgent === 'maybe' || data.estateAgentNo === 'yes');
    const isFullContact =
        step.name === 'contact' && data.firstName && data.lastName && data.email && (!withEstateAgent || (withEstateAgent && data.dataProcessingAccepted));
    const isValid = !step.isValid || step.isValid(data[step.name]);
    const enableNextButton =
        isValid && ((step.name !== 'precalc' && data[step.name]) || data[step.name] >= 5 || step.features || isFullAdress || isFullContact);

    let description: string | React.ReactNode = replaceString(step.description, data.code);
    if (step.name === 'contact' && withEstateAgent) {
        description = <RenderWithEstateDescription buttonText="Zur Bewertung" />;
    }
    return (
        <div className={styles.valuationWidget}>
            <HtmlElement tag={step.name === 'loggedIn' ? 'div' : 'form'} onSubmit={handleSubmit}>
                <div className={styles.content}>
                    <div className={styles.teaser}>
                        <div className={styles.text}>
                            <div className={styles.scrollIntoView} ref={topRef} />
                            <h1>{replaceString(step.title, data.code)}</h1>
                            {description && (
                                <div className={styles.description}>
                                    {description || '\u00a0'}
                                    {step.tooltip && (
                                        <>
                                            {' '}
                                            <Tooltip>{step.tooltip}</Tooltip>
                                        </>
                                    )}
                                </div>
                            )}

                            {isLoading && <LoadingIndicator className={styles.loadingIndicator} />}

                            {/* {step.name === 'score' && renderScore} */}
                        </div>
                        <div className={styles.image}>
                            {/* <img src={step.image} /> */}
                            <Image importPromise={step.image} />
                        </div>
                    </div>

                    {stepIdx > 0 && <div className={styles.template}>{renderStep({ step, data, nextStep, onChange, setData, score, valuationId })}</div>}
                    {/* {stepIdx > 0 && <div className={styles.template}>{template}</div>} */}
                </div>
                <div className={styles.actions}>
                    {/* {stepIdx > 0 && <Button type="button" size="big" onClick={prevStep} color="secondary" icon="chevron_left"></Button>} */}
                    {stepIdx > 0 && <ButtonRound type="button" size="extraLarge" onClick={prevStep} color="white" icon={<ChevronLeftIcon />}></ButtonRound>}

                    {stepIdx === 0 ? (
                        <ButtonRound size="extraLarge">Los geht's!</ButtonRound>
                    ) : step.isLast ? (
                        user.isLoggedIn || variant === 'default' ? (
                            <ButtonRound
                                onClick={() => {
                                    if (variant === 'dashboard') {
                                        // usecase: when user is already logged in
                                        sendData();
                                    }
                                }}
                                // disabled={!enableNextButton}
                                size="extraLarge"
                                color="primary"
                                // variant="outlined"
                                // icon="chevron_right"
                                type="submit"
                                variant={'outlined'}
                                isLoading={false}
                            >
                                Zur Bewertung
                            </ButtonRound>
                        ) : null
                    ) : enableNextButton ? (
                        <ButtonRound
                            onClick={() => {
                                // nextStep();
                            }}
                            disabled={!enableNextButton}
                            size="extraLarge"
                            color="primary"
                            // variant="outlined"
                            // icon="chevron_right"
                            type="submit"
                            variant={'default'}
                            isLoading={false}
                        >
                            Weiter
                        </ButtonRound>
                    ) : (
                        <ButtonRound className={styles.hidden} />
                    )}
                </div>
            </HtmlElement>
        </div>
    );
};

type IRenderStepProps = {
    step: IStep;
    data: IValuationData;
    nextStep: (name: IStep['name'], value: string | number) => void;
    onChange: (name: IStep['name'], value: string | boolean) => void;
    setData: React.Dispatch<React.SetStateAction<IValuationData>>;
    score: IScore;
    valuationId: string;
};

const renderStep: React.FC<React.PropsWithChildren<IRenderStepProps>> = ({ step, data, nextStep, onChange, setData, score, valuationId }) => {
    const isValid = !step.isValid || step.isValid(data[step.name]);

    switch (step?.name) {
        case 'code':
            return <ValuationCode nextStep={nextStep} data={data} />;
        case 'subcode':
        case 'condition':
        case 'dealType':
        case 'reason':
        case 'estateAgent':
        case 'estateAgentNo':
            return <ValuationOptions step={step} nextStep={nextStep} data={data} />;
        case 'livingArea':
        case 'landArea':
        case 'buildingYear':
        case 'numberOfRooms':
        case 'numberOfBathrooms':
            return (
                <div className={styles.input}>
                    {/* <label htmlFor={step.name}>{step.label}</label> */}
                    <NumberField
                        key={step.name}
                        // onValueChange={(value) => {
                        //     onChange(step.name, value.value);
                        // }}
                        onChange={(name, value) => {
                            onChange(name as IStep['name'], value?.toString() || '');
                        }}
                        label=""
                        allowEmptyFormatting
                        name={step.name}
                        value={data[step.name] ? Number(data[step.name]) : ''}
                        // displayType={'input'}
                        thousandSeparator={step.name === 'buildingYear' ? null : '.'}
                        // decimalSeparator={','}
                        // allowedDecimalSeparators={[',', '.']}
                        inputMode={'decimal'}
                        // onValueChange={handlePercetageChange}
                        decimalScale={step.decimalScale}
                        disabled={false}
                        suffix={step.unit ? ` ${step.unit}` : ''}
                        autoFocus={location.hash.indexOf('step') > 0}
                        error={!isValid}
                        helperText={data[step.name] && !isValid ? step.validHint : ''}
                    />
                </div>
            );

        case 'features':
            return (
                <div className={styles.features}>
                    {Object.keys(step.features[data.code]).map((key) => {
                        const feature = step.features[data.code][key];
                        const isChecked = !!data[key];
                        return (
                            <div
                                key={key}
                                className={styles.feature + (isChecked ? ' ' + styles.active : '')}
                                onClick={() => {
                                    setData({ ...data, [key]: data[key] ? 0 : 1 });
                                }}
                            >
                                <Check checked={isChecked} />
                                <span>{feature}</span>
                            </div>
                        );
                    })}
                </div>
            );

        case 'address':
            return (
                <div>
                    <Input label="Straße und Hausnummer" name="street" value={data.street} onChange={onChange} />
                    <Input label="Postleitzahl" name="postCode" inputMode="numeric" pattern={inputPatterns.postalCode} value={data.postCode} onChange={onChange} />
                    <Input label="Ort" name="city" value={data.city} onChange={onChange} />
                </div>
            );

        case 'precalc':
            return <Precalc nextStep={nextStep} />;

        case 'contact':
            return <Contact data={data} onChange={onChange} />;

        case 'loggedIn':
            const trackingEvent = {
                'event': 'login_register',
                'event_name': 'login_register',
                'streckenzweig1': 'Registrieren',
                'streckenzweig2': location.pathname,
                'counter': 1,
            }
            return <LoginRegister onChange={onChange} trackingEvent={trackingEvent} />;
            break;
        case 'score':
            const currency = score?.currency === 'EUR' ? '€' : score?.currency;

            return (
                <div>
                    {!score ? (
                        <div className={styles.score}>
                            <p>Aufbereitung der Daten</p>
                            <LoadingIndicator />
                        </div>
                    ) : (
                        <>
                            <p className={styles.result}>
                                {data.reason === 'buy' || data.reason === 'sell' ? 'Immobilienwert' : 'Mietpreisspanne'}:
                                <br />
                                <b>
                                    ca. {score?.valueRange?.lower?.toLocaleString('de-DE')} {currency}
                                </b>{' '}
                                bis{' '}
                                <b>
                                    {score?.valueRange?.upper?.toLocaleString('de-DE')} {currency}
                                </b>
                            </p>

                            <p>
                                <DossierButton uuid={valuationId} dossierUrl={null} data={data}>
                                    Detailbewertung ansehen
                                </DossierButton>
                            </p>
                        </>
                    )}
                    {renderScore(data)}
                </div>
            );
    }
};

export const RenderWithEstateDescription: React.FC<React.PropsWithChildren<{ buttonText: string }>> = ({ buttonText = 'Jetzt anfragen' }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={styles.withEstate}>
            <p>Ein Experte meldet sich bei Dir.</p>
            <a
                className={isOpen ? styles.open : ''}
                onClick={() => {
                    setIsOpen(!isOpen);
                }}
            >
                <span>Was bedeutet das?</span> <KeyboardArrowRightIcon />
            </a>
            <p>
                Volle Kraft voraus für Deine Immobilie! Gerne helfen wir Dir dabei, Deine Ziele und Ideen zu verwirklichen. Einfach Kontaktformular ausfüllen,
                auf "{buttonText}" klicken und schon bald meldet sich unser Experten-Team bei Dir zurück!
                <br />
                <br />
                Good to know: Unsere Kontaktaufnahme ist zu jedem Zeitpunkt kostenlos und (wirklich!) unverbindlich. Wir freuen uns, von Dir zu hören!
            </p>
        </div>
    );
};

export { renderStep, replaceString };
export default ValuationWidget;
